/**
 * Album Service - Frontend
 * Handles trip photo albums and photo management
 */

import { API_BASE } from '../config/api';
import AuthService from './authService';
import { toDraftBody } from '../utils/recapMedia';

class AlbumService {
  static API_BASE = API_BASE;

  // Use centralized AuthService.handleResponse for auth error handling

  // =====================================================
  // USER ALBUMS
  // =====================================================

  /**
   * Get all albums created by the current user (across all trips)
   * @returns {array} Array of albums
   */
  static async getUserAlbums() {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/user`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.albums || [];
    } catch (error) {
      console.error('Error getting user albums:', error);
      return [];
    }
  }

  /**
   * Get albums saved from user's travel boards (from other users)
   * @returns {array} Array of albums with creator info
   */
  static async getAlbumsFromBoards() {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/from-boards`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.albums || [];
    } catch (error) {
      console.error('Error getting albums from boards:', error);
      return [];
    }
  }

  /**
   * Get albums the user has explicitly saved (via travel boards).
   * Albums the user merely authored via the trip-review flow are NOT returned
   * here — authoring an album does not imply saving it to My Saved Items.
   * @returns {array} Array of saved albums with creator info
   */
  static async getAllUserAlbums() {
    try {
      // is_owned is resolved server-side (it knows the viewer), so this is a
      // pass-through — no second round trip to identify the current user.
      return await this.getAlbumsFromBoards();
    } catch (error) {
      console.error('Error getting all user albums:', error);
      return [];
    }
  }

  // =====================================================
  // ALBUM CRUD OPERATIONS
  // =====================================================

  /**
   * Create a new album
   * @param {object} albumData - Album details
   * @returns {object} Result with created album
   */
  static async createAlbum(albumData) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify(albumData)
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error creating album:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get albums for a trip
   * @param {string} tripId - Trip ID
   * @returns {array} Array of albums
   */
  static async getTripAlbums(tripId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/trip/${tripId}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.albums || [];
    } catch (error) {
      console.error('Error getting trip albums:', error);
      return [];
    }
  }

  /**
   * Get trip photo pool (all available photos for album building)
   * @param {string} tripId - Trip ID
   * @param {object} [options] - { tripDayIds?: number[] }
   * @returns {array} Array of photos
   */
  static async getTripPhotoPool(tripId, options = {}) {
    try {
      const { tripDayIds } = options;
      let url = `${this.API_BASE}/api/albums/trip/${tripId}/photos`;
      if (Array.isArray(tripDayIds) && tripDayIds.length > 0) {
        url += `?tripDayIds=${tripDayIds.join(',')}`;
      }
      const response = await fetch(url, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.photos || [];
    } catch (error) {
      // Rethrow rather than returning [] — an empty pool and a failed fetch
      // are different facts, and a caller rendering the pool must not treat
      // a network error as "this trip has no photos" (a failed refetch after
      // a batch delete used to blank the whole composer grid).
      console.error('Error getting trip photo pool:', error);
      throw error;
    }
  }

  /**
   * Get a single album by ID with photos
   * @param {string} albumId - Album ID
   * @returns {object|null} Album with photos
   */
  static async getAlbumById(albumId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/${albumId}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.album || null;
    } catch (error) {
      console.error('Error getting album:', error);
      return null;
    }
  }

  /**
   * Update an album
   * @param {string} albumId - Album ID
   * @param {object} updates - Fields to update
   * @returns {object} Result with updated album
   */
  static async updateAlbum(albumId, updates) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/${albumId}`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify(updates)
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error updating album:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete an album
   * @param {string} albumId - Album ID
   * @returns {object} Result
   */
  static async deleteAlbum(albumId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/${albumId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error deleting album:', error);
      return { success: false, error: error.message };
    }
  }

  // =====================================================
  // ALBUM PHOTO MANAGEMENT
  // =====================================================

  /**
   * Sync photos in an album (replace all)
   * Used when editing an album to update the complete photo list
   * @param {string} albumId - Album ID
   * @param {array} photoIds - Array of photo IDs (replaces existing)
   * @returns {object} Result
   */
  static async syncAlbumPhotos(albumId, photoIds, coverPhotoId, { media, coverVideoId } = {}) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/${albumId}/photos/sync`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        // coverPhotoId is optional: omit it and the server falls back to
        // photoIds[0]. Callers that let the user pick a cover independently of
        // position (AlbumEdit) must send it, or the sync silently relocates the
        // cover to whichever photo happens to sort first.
        // media / coverVideoId (VIDEO_PLAN V5): photos and your videos in one
        // order, and a video cover; omitted, the album's videos stay as they are.
        body: JSON.stringify({
          photoIds,
          ...(coverPhotoId == null ? {} : { coverPhotoId }),
          ...(Array.isArray(media) ? { media } : {}),
          ...(coverVideoId !== undefined ? { coverVideoId } : {})
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error syncing album photos:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Add photos to an album
   * @param {string} albumId - Album ID
   * @param {array} photoIds - Array of photo IDs to add
   * @returns {object} Result
   */
  static async addPhotosToAlbum(albumId, photoIds) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/${albumId}/photos`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ photoIds })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error adding photos to album:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Remove a photo from an album
   * @param {string} albumId - Album ID
   * @param {number} photoId - Photo ID to remove
   * @returns {object} Result
   */
  static async removePhotoFromAlbum(albumId, photoId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/${albumId}/photos/${photoId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error removing photo from album:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Reorder photos in an album
   * @param {string} albumId - Album ID
   * @param {array} photoOrder - Array of {photoId, displayOrder} objects
   * @returns {object} Result
   */
  static async reorderAlbumPhotos(albumId, photoOrder) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/${albumId}/photos/reorder`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ photoOrder })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error reordering album photos:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update custom caption for a photo in an album
   * @param {string} albumId - Album ID
   * @param {number} photoId - Photo ID
   * @param {string} caption - Custom caption
   * @returns {object} Result
   */
  static async updatePhotoCaption(albumId, photoId, caption) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/${albumId}/photos/${photoId}/caption`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ caption })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error updating photo caption:', error);
      return { success: false, error: error.message };
    }
  }

  // =====================================================
  // HELPER METHODS
  // =====================================================

  /**
   * Album type presets with friendly names
   */
  static ALBUM_TYPES = {
    highlights: { value: 'highlights', label: 'Trip Highlights' },
    food_tour: { value: 'food_tour', label: 'Food Tour' },
    family: { value: 'family', label: 'Family Moments' },
    tourist: { value: 'tourist', label: 'Tourist Spots' },
    nature: { value: 'nature', label: 'Nature & Outdoors' },
    nightlife: { value: 'nightlife', label: 'Nightlife' },
    custom: { value: 'custom', label: 'Custom' }
  };

  /**
   * Get album type label
   * @param {string} albumType - Album type value
   * @returns {string} Friendly label
   */
  static getAlbumTypeLabel(albumType) {
    return this.ALBUM_TYPES[albumType]?.label || 'Custom';
  }

  // =====================================================
  // ARCHIVE METHODS
  // =====================================================

  // Albums are not archivable. archiveAlbum/unarchiveAlbum lived here and
  // their endpoints are gone: trip_albums.trip_id is NOT NULL, so an album is
  // always reachable from its trip and the Planner Past tab, and it already
  // carries its own status (draft/published) and visibility controls. The
  // archive it had never worked — getTripAlbums has no archived_at filter, so
  // an archived album vanished from Saved while staying on the trip page.

  // =====================================================
  // RECAP PUBLISH (server still uses the draft → publish two-step under
  // the hood; the client just chains them in `handlePublish` so the user
  // never sees the intermediate draft state).
  // =====================================================

  /**
   * Create-or-update the album row in 'draft' status. Used as the first half
   * of the one-shot publish flow.
   * @param {string} tripId
   * @param {object} draftData - { albumName?, albumDescription?, visibility?, coverPhotoId?, tripDayIds?, photoIds?, photoCaptions? }
   *   photoCaptions: { [photoId]: string } map of per-photo custom captions.
   */
  static async upsertDraft(tripId, draftData) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/trip/${tripId}/draft`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        // A selection may hold videos (`v:<id>` keys): sent as photoIds + media.
        body: JSON.stringify(toDraftBody(draftData))
      });
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error upserting draft:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Flip a draft album to 'published'. Second half of the one-shot publish.
   */
  static async publishDraft(albumId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/${albumId}/publish`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders()
      });
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error publishing draft:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get the current user's draft recap for a trip (one per trip).
   * Returns the draft album with photos, or null.
   */
  static async getDraft(tripId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/trip/${tripId}/draft`, {
        headers: AuthService.getJsonHeaders()
      });
      const result = await AuthService.handleResponse(response);
      return result?.draft || null;
    } catch (error) {
      console.error('Error getting draft:', error);
      return null;
    }
  }

  /**
   * Discard the current user's draft recap for a trip.
   */
  static async discardDraft(tripId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/trip/${tripId}/draft`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error discarding draft:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * GPS-based activity-tag suggestions for the trip's untagged photos.
   * Returns [{ photo_id, activity_id, activity_name, distance_m, date_match }].
   */
  static async getPhotoTagSuggestions(tripId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/trip/${tripId}/photo-tag-suggestions`, {
        headers: AuthService.getJsonHeaders()
      });
      const result = await AuthService.handleResponse(response);
      return Array.isArray(result?.suggestions) ? result.suggestions : [];
    } catch (error) {
      console.error('Error getting photo tag suggestions:', error);
      return [];
    }
  }

  /**
   * Per-trip recap progress summary ("where you left off"). Returns the
   * progress object or null.
   */
  static async getRecapProgress(tripId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/albums/trip/${tripId}/recap-progress`, {
        headers: AuthService.getJsonHeaders()
      });
      const result = await AuthService.handleResponse(response);
      return result?.progress || null;
    } catch (error) {
      console.error('Error getting recap progress:', error);
      return null;
    }
  }
}

export default AlbumService;
